import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, delay, switchMap, catchError } from 'rxjs';
import { LoggerService } from '@victory-lib-ui/shared/utils';
import { ENVIRONMENT_CONFIG } from '../config/environment.config';
import { MockService } from '../mock/mock.service';

/**
 * Mock Interceptor for Demo Environment
 * Intercepts HTTP requests and returns mock data
 * Only active when environment.demo is true
 */
export const mockInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const logger = inject(LoggerService);
  const environment = inject(ENVIRONMENT_CONFIG);
  const mockService = inject(MockService);

  const isDemoMode =
    environment.demo ||
    environment.features.enableMockData ||
    sessionStorage.getItem('victory_lib_demo_mode') === 'true';

  if (!isDemoMode || isAssetRequest(req.url)) {
    return next(req);
  }

  logger.info('Mock Interceptor: Intercepting request', { url: req.url });

  return mockService.resolve(req).pipe(
    switchMap((mockResult) => {
      if (!mockResult) {
        logger.warn('Mock Interceptor: No mock route matched request', {
          method: req.method,
          url: req.url,
        });

        return of(createUnhandledMockResponse(req));
      }

      logger.debug('Mock Interceptor: Returning mock response', mockResult);
      const apiDelay = environment.demoConfig?.apiDelay || 500;

      return of(
        new HttpResponse({
          status: mockResult.status ?? 200,
          body: mockResult.body,
        })
      ).pipe(delay(apiDelay));
    }),
    catchError((error) => {
      logger.error('Mock Interceptor: Failed to resolve mock response', error);

      return of(
        new HttpResponse({
          status: 500,
          body: {
            success: false,
            message: 'Demo mock failed to process the request',
            data: null,
          },
        })
      );
    })
  );
};

const isAssetRequest = (url: string): boolean => {
  const parsedUrl = new URL(url, window.location.origin);
  return parsedUrl.pathname.startsWith('/assets/');
};

const createUnhandledMockResponse = (
  req: HttpRequest<unknown>
): HttpResponse<unknown> =>
  new HttpResponse({
    status: 404,
    body: {
      success: false,
      message: 'No demo mock route configured for this request',
      data: {
        method: req.method,
        path: req.url,
      },
    },
  });
